import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

// React Icons
import {
    FiPlus,
    FiEdit,
    FiTrash2,
    FiCheck,
    FiX,
    FiFolder,
    FiBriefcase,
    FiRefreshCw,
    FiSearch,
    FiDownload
} from "react-icons/fi";

import {
    MdCategory,
    MdOutlineCategory,
    MdOutlineLibraryAdd,
    MdOutlineDeleteSweep
} from "react-icons/md";

import { TbCategoryPlus } from "react-icons/tb";
import { HiOutlineDocumentAdd } from "react-icons/hi";

import "../styles/PracticeAreasManagement.css";

export default function PracticeAreasManagement() {
    const { token } = useAuth();

    /* ======================
       STATE
    ====================== */
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [newCategory, setNewCategory] = useState("");
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingCategoryValue, setEditingCategoryValue] = useState("");
    const [newArea, setNewArea] = useState({});
    const [editingArea, setEditingArea] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    const [exportLoading, setExportLoading] = useState(false);

    /* ======================
       FETCH DATA (REMOVED PAGINATION)
    ====================== */
    const fetchData = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                ...(searchTerm && { search: searchTerm })
            }).toString();

            const res = await fetch(`${API_BASE_URL}/api/practice-areas?${queryParams}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const json = await res.json();
            setData(json.data || []);
        } catch (err) {
            toast.error("Failed to load practice areas");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const handleRefresh = async () => {
        setSearchTerm("");   // clear search
        await fetchData();   // reload full data
    };

    const exportCount = data.length;

    /* ======================
       EXPORT TO EXCEL
    ====================== */
    const handleExport = async () => {
        try {
            setExportLoading(true);
            const params = new URLSearchParams({
                search: searchTerm,
                export: "excel"
            }).toString();

            const response = await fetch(
                `${API_BASE_URL}/api/practice-areas/export?${params}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `practice_areas_${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            toast.success("Excel downloaded successfully");
        } catch (error) {
            toast.error("Excel export failed");
        } finally {
            setExportLoading(false);
        }
    };

    /* ======================
       CATEGORY OPERATIONS
    ====================== */
    const addCategory = async () => {
        if (!newCategory.trim()) {
            toast.error("Please enter a category name");
            return;
        }

        try {
            await fetch(`${API_BASE_URL}/api/practice-areas/category`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ category: newCategory })
            });

            toast.success("Category added successfully");
            setNewCategory("");
            fetchData();
        } catch (error) {
            toast.error("Failed to add category");
        }
    };

    const updateCategory = async (oldCategory) => {
        if (!editingCategoryValue.trim()) {
            toast.error("Category name cannot be empty");
            return;
        }

        try {
            await fetch(`${API_BASE_URL}/api/practice-areas/category`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    oldCategory,
                    newCategory: editingCategoryValue
                })
            });

            toast.success("Category updated successfully");
            setEditingCategory(null);
            setEditingCategoryValue("");
            fetchData();
        } catch (error) {
            toast.error("Failed to update category");
        }
    };

    const deleteCategory = async (categoryName) => {
        const result = await Swal.fire({
            title: "Delete Category?",
            html: `<p>You are about to delete:</p>
                   <strong>${categoryName}</strong>
                   <p class="delete-warning">This will also delete all areas under this category!</p>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
            reverseButtons: true,
            customClass: {
                confirmButton: 'sweetalert-confirm-btn',
                cancelButton: 'sweetalert-cancel-btn'
            }
        });

        if (!result.isConfirmed) return;

        try {
            await fetch(`${API_BASE_URL}/api/practice-areas/category`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ category: categoryName })
            });

            toast.success("Category deleted successfully");
            fetchData();
        } catch (error) {
            toast.error("Failed to delete category");
        }
    };

    /* ======================
       AREA OPERATIONS
    ====================== */
    const addArea = async (categoryName) => {
        const area = newArea[categoryName];
        if (!area?.trim()) {
            toast.error("Please enter an area name");
            return;
        }

        try {
            await fetch(`${API_BASE_URL}/api/practice-areas/area`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    category: categoryName,
                    area
                })
            });

            toast.success("Area added successfully");
            setNewArea(prev => ({ ...prev, [categoryName]: "" }));
            fetchData();
        } catch (error) {
            toast.error("Failed to add area");
        }
    };

    const updateArea = async (categoryName) => {
        if (!editingValue.trim()) {
            toast.error("Area name cannot be empty");
            return;
        }

        try {
            await fetch(`${API_BASE_URL}/api/practice-areas/area`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    category: categoryName,
                    oldArea: editingArea.area,
                    newArea: editingValue
                })
            });

            toast.success("Area updated successfully");
            setEditingArea(null);
            setEditingValue("");
            fetchData();
        } catch (error) {
            toast.error("Failed to update area");
        }
    };

    const deleteArea = async (categoryName, area) => {
        const result = await Swal.fire({
            title: "Delete Area?",
            text: "This action cannot be undone",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            reverseButtons: true,
            customClass: {
                confirmButton: 'sweetalert-confirm-btn',
                cancelButton: 'sweetalert-cancel-btn'
            }
        });

        if (!result.isConfirmed) return;

        try {
            await fetch(`${API_BASE_URL}/api/practice-areas/area`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    category: categoryName,
                    area
                })
            });

            toast.success("Area deleted successfully");
            fetchData();
        } catch (error) {
            toast.error("Failed to delete area");
        }
    };

    /* ======================
       HEADER STATS COMPONENT
    ====================== */
    const renderHeaderStats = () => {
        const totalCategories = data.length;
        const totalAreas = data.reduce((sum, cat) => sum + (cat.areas?.length || 0), 0);

        return (
            <div className="practice-stats-grid">
                <div className="practice-stat-card practice-stat-blue">
                    <div className="practice-stat-icon-wrapper">
                        <MdCategory className="practice-stat-icon" />
                    </div>
                    <div className="practice-stat-content">
                        <h3 className="practice-stat-value">{totalCategories}</h3>
                        <p className="practice-stat-label">Total Categories</p>
                        <div className="practice-stat-breakdown">
                            <span className="practice-stat-subtext active">
                                {data.filter(cat => cat.areas?.length > 0).length} With Areas
                            </span>
                        </div>
                    </div>
                </div>

                <div className="practice-stat-card practice-stat-green">
                    <div className="practice-stat-icon-wrapper">
                        <FiBriefcase className="practice-stat-icon" />
                    </div>
                    <div className="practice-stat-content">
                        <h3 className="practice-stat-value">{totalAreas}</h3>
                        <p className="practice-stat-label">Practice Areas</p>
                        <span className="practice-stat-subtext">All categories</span>
                    </div>
                </div>

                <div className="practice-stat-card practice-stat-orange">
                    <div className="practice-stat-icon-wrapper">
                        <FiFolder className="practice-stat-icon" />
                    </div>
                    <div className="practice-stat-content">
                        <h3 className="practice-stat-value">
                            {data.reduce((max, cat) => Math.max(max, cat.areas?.length || 0), 0)}
                        </h3>
                        <p className="practice-stat-label">Most Areas</p>
                        <span className="practice-stat-subtext">In a single category</span>
                    </div>
                </div>

                <div className="practice-stat-card practice-stat-red">
                    <div className="practice-stat-icon-wrapper">
                        <MdOutlineDeleteSweep className="practice-stat-icon" />
                    </div>
                    <div className="practice-stat-content">
                        <h3 className="practice-stat-value">
                            {data.filter(cat => !cat.areas || cat.areas.length === 0).length}
                        </h3>
                        <p className="practice-stat-label">Empty</p>
                        <span className="practice-stat-subtext">Require attention</span>
                    </div>
                </div>
            </div>
        );
    };

    /* ======================
       RENDER CATEGORY CARD
    ====================== */
    const renderCategoryCard = (cat) => (
        <div key={cat._id} className="practice-category-card">
            {/* CATEGORY HEADER */}
            <div className="practice-category-header">
                {editingCategory === cat.category ? (
                    <div className="practice-edit-form">
                        <input
                            type="text"
                            value={editingCategoryValue}
                            onChange={(e) => setEditingCategoryValue(e.target.value)}
                            className="practice-edit-input"
                            placeholder="Category name"
                            autoFocus
                        />
                        <div className="practice-edit-actions">
                            <button
                                className="practice-save-btn"
                                onClick={() => updateCategory(cat.category)}
                            >
                                <FiCheck size={16} />
                                <span>Save</span>
                            </button>
                            <button
                                className="practice-cancel-btn"
                                onClick={() => {
                                    setEditingCategory(null);
                                    setEditingCategoryValue("");
                                }}
                            >
                                <FiX size={16} />
                                <span>Cancel</span>
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="practice-category-title">
                            <div className="practice-category-icon">
                                <FiFolder size={20} />
                            </div>
                            <div className="practice-category-details">
                                <h3 className="practice-category-name">{cat.category}</h3>
                                <div className="practice-category-meta">
                                    <span className="practice-category-count">
                                        {cat.areas?.length || 0} areas
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="practice-category-actions">
                            <button
                                className="practice-action-btn practice-edit-btn"
                                onClick={() => {
                                    setEditingCategory(cat.category);
                                    setEditingCategoryValue(cat.category);
                                }}
                                title="Edit Category"
                            >
                                <FiEdit size={16} />
                                <span>Edit</span>
                            </button>
                            <button
                                className="practice-action-btn practice-delete-btn"
                                onClick={() => deleteCategory(cat.category)}
                                title="Delete Category"
                            >
                                <FiTrash2 size={16} />
                                <span>Delete</span>
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* ADD AREA FORM */}
            <div className="practice-add-area-form">
                <div className="practice-add-area-input-wrapper">
                    <HiOutlineDocumentAdd className="practice-add-area-icon" />
                    <input
                        type="text"
                        placeholder="Add new practice area"
                        value={newArea[cat.category] || ""}
                        onChange={(e) =>
                            setNewArea(prev => ({
                                ...prev,
                                [cat.category]: e.target.value
                            }))
                        }
                        className="practice-add-area-input"
                        onKeyPress={(e) => e.key === 'Enter' && addArea(cat.category)}
                    />
                </div>
                <button
                    className="practice-add-area-btn"
                    onClick={() => addArea(cat.category)}
                    disabled={!newArea[cat.category]?.trim()}
                >
                    <FiPlus size={16} />
                    <span>Add Area</span>
                </button>
            </div>

            {/* AREAS LIST */}
            <div className="practice-areas-list">
                {!cat.areas || cat.areas.length === 0 ? (
                    <div className="practice-no-areas">
                        <FiBriefcase size={24} />
                        <p>No practice areas yet</p>
                        <small>Add your first area using the form above</small>
                    </div>
                ) : (
                    <ul className="practice-areas-ul">
                        {cat.areas.map(area => (
                            <li key={area} className="practice-area-item">
                                {editingArea?.area === area &&
                                    editingArea?.category === cat.category ? (
                                    <div className="practice-edit-area-form">
                                        <input
                                            type="text"
                                            value={editingValue}
                                            onChange={(e) => setEditingValue(e.target.value)}
                                            className="practice-edit-area-input"
                                            autoFocus
                                        />
                                        <div className="practice-edit-area-actions">
                                            <button
                                                className="practice-save-area-btn"
                                                onClick={() => updateArea(cat.category)}
                                            >
                                                <FiCheck size={14} />
                                            </button>
                                            <button
                                                className="practice-cancel-area-btn"
                                                onClick={() => {
                                                    setEditingArea(null);
                                                    setEditingValue("");
                                                }}
                                            >
                                                <FiX size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="practice-area-content">
                                            <FiBriefcase className="practice-area-icon" />
                                            <span className="practice-area-text">{area}</span>
                                        </div>
                                        <div className="practice-area-actions">
                                            <button
                                                className="practice-edit-area-btn"
                                                onClick={() => {
                                                    setEditingArea({
                                                        category: cat.category,
                                                        area
                                                    });
                                                    setEditingValue(area);
                                                }}
                                                title="Edit Area"
                                            >
                                                <FiEdit size={14} />
                                            </button>
                                            <button
                                                className="practice-delete-area-btn"
                                                onClick={() => deleteArea(cat.category, area)}
                                                title="Delete Area"
                                            >
                                                <FiTrash2 size={14} />
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );

    /* ======================
       MAIN RENDER
    ====================== */
    return (
        <div className="luxury-practice-management">

            <div className="practice-areas-header">
                <div className="practice-areas-header-left">
                    <h1>Practice Areas Management</h1>
                    <p className="practice-areas-page-subtitle">
                        Manage legal practice categories and areas
                    </p>
                </div>

                <div className="practice-areas-header-right">
                    <button
                        className="practice-areas-refresh-btn"
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        <FiRefreshCw />
                        {loading ? "Refreshing..." : "Refresh"}
                    </button>

                    <button
                        className="practice-areas-export-btn"
                        onClick={handleExport}
                        disabled={exportLoading || data.length === 0}
                    >
                        <FiDownload />
                        {exportLoading
                            ? "Exporting..."
                            : `Export (${exportCount})`}
                    </button>
                </div>
            </div>




            {/* HEADER STATS */}
            {renderHeaderStats()}

            {/* MAIN CONTENT */}
            <div className="practice-management-content">
                {/* CONTROLS */}
                <div className="practice-controls">
                    <div className="practice-controls-left">
                        <div className="practice-add-category-form">
                            <div className="practice-add-category-input-wrapper">
                                <TbCategoryPlus className="practice-add-category-icon" />
                                <input
                                    type="text"
                                    placeholder="Add new category"
                                    value={newCategory}
                                    onChange={(e) => setNewCategory(e.target.value)}
                                    className="practice-add-category-input"
                                    onKeyPress={(e) => e.key === 'Enter' && addCategory()}
                                />
                            </div>
                            <button
                                className="practice-add-category-btn"
                                onClick={addCategory}
                                disabled={!newCategory.trim()}
                            >
                                <MdOutlineLibraryAdd size={18} />
                                <span>Add Category</span>
                            </button>
                        </div>
                    </div>

                    <div className="practice-controls-right">
                        <div className="practice-search-box">
                            <FiSearch className="practice-search-icon" />
                            <input
                                type="text"
                                placeholder="Search categories or areas..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="practice-search-input"
                            />
                        </div>
                    </div>
                </div>

                {/* CATEGORIES LIST */}
                <div className="practice-categories-container">
                    {loading ? (
                        <div className="practice-loading-state">
                            <div className="practice-loading-spinner"></div>
                            <p>Loading practice areas...</p>
                        </div>
                    ) : data.length === 0 ? (
                        <div className="practice-no-data">
                            <MdOutlineCategory size={48} />
                            <p>No practice categories found</p>
                            <small>Add your first category to get started</small>
                        </div>
                    ) : (
                        data.map(renderCategoryCard)
                    )}
                </div>
            </div>
        </div>
    );
}